import { useQuery } from 'react-query'
import { useAuth0 } from '../../../contexts/auth0-context'
import callAPI from '../../../API/callSecureAPI'

export type AnalyticsRoadmap = {
    _id: string
    product: string
    indication: string
    isActive: boolean
    organizationId: string
    organizationName: string
    createdAt: string
    updatedAt: string
    vmCount: number
    evidenceCount: number
    activityCount: number
    totalContent: number
    lastActivity: string
    recentChanges: number
}

export type AnalyticsOrganization = {
    organizationId: string
    organizationName: string
    isFavorite: boolean
    roadmaps: AnalyticsRoadmap[]
    // User activity metrics (last 30 days)
    userEventCount: number
    activeUserCount: number
    lastUserActivity: string | null
    // Roadmap activity metrics (last 30 days)
    totalRoadmapChanges: number
    // Combined activity score
    totalActivityScore: number
}

export type AnalyticsResponse = {
    success: boolean
    data: {
        organizations: AnalyticsOrganization[]
        totalRoadmaps: number
    }
}

const FIVE_MINUTES = 1000 * 60 * 5

export const useAdminAnalyticsQuery = ({
    includeInternal = false,
    actionTypes,
}: { includeInternal?: boolean; actionTypes?: string[] } = {}) => {
    const { getTokenSilently, isAuthenticated, isLoading } = useAuth0()

    const getAnalytics = async () => {
        try {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })
            const params = new URLSearchParams()
            if (includeInternal) params.set('includeInternal', 'true')
            if (actionTypes && actionTypes.length > 0) params.set('actionTypes', actionTypes.join(','))
            const url = `/api/admin/analytics/roadmaps${params.toString() ? `?${params.toString()}` : ''}`
            const response = await callAPI(url, 'get', token)
            return response
        } catch (e) {
            console.error('Error fetching analytics:', e)
            throw e
        }
    }

    return useQuery<AnalyticsResponse>(
        ['admin', 'analytics', 'roadmaps', { includeInternal, actionTypes }],
        getAnalytics,
        {
            enabled: !!getTokenSilently && !!isAuthenticated && !isLoading,
            cacheTime: FIVE_MINUTES,
            staleTime: 60000,
            retry: 2,
        }
    )
}
